import {DestroyRef, Inject, Injectable, NgZone, DOCUMENT} from '@angular/core';
import {catchError, concatMap, map, Observable, of, take, tap, throwError} from 'rxjs';
import {DisplayPreferences} from '../../common/models/display-preferences.model';
import {AppConfigService} from '../../common/service/app-config.service';
import {SlipService} from './slip-service';
import {CustomLookupService} from '../../common/service/custom-lookup.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {PrintAreaService} from '../../common/service/print-area.service';
import {MapModeService} from './map-mode.service';
import {MapToolsService} from './map-tools.service';
import {DisplayPreferencesService} from '../../common/service/display-preferences.service';
import {LineLayerService} from '../layers/line-layer.service';
import {PointLayerService} from '../layers/point-layer.service';
import {ControlPointLayerService} from '../layers/control-point-layer.service';
import {ConstructPointLayerService} from '../layers/construct-point-layer.service';
import {AngleLayerService} from '../layers/angle-layer.service';
import {AnnotationLayerService} from '../layers/annotation-layer.service';
import {SpatialUtilService} from './spatial-util.service';
import {PolylineLayerService} from '../layers/polyline-layer.service';
import {EnlargementLayerService} from '../layers/enlargement-layer.service';
import {ClippingLayerService} from '../layers/clipping-layer.service';
import {PrintAreaLayerService} from '../layers/print-area-layer.service';
import {LotLayerService} from '../layers/lot-layer.service';
import {RoadLayerService} from '../layers/road-layer.service';
import {InterestLayerService} from '../layers/interest-layer.service';
import {MapEventsService} from './map-events.service';
import {equalsIgnoreAttrsValues, isDefined, isFunction, isNumber} from 'src/app/common/service/data-util';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {EsriModuleService} from '../esri-nlr/services/esri-module.service';

import {LookupService} from '../../common/service/lookup.service';
import {ActivatedRoute, Router} from '@angular/router';
import {PolygonLayerService} from '../layers/polygon-layer.service';
import {ArcGisAuthService} from './arc-gis-auth.service';
import {ConstructLineLayerService} from '../layers/construct-line-layer.service';
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/no-unused-vars */
export interface LayerOption {
    URL: string;
    OPTIONS: {
        id: string;
        outFields: string[];
        mode: number;
    };
}

@Injectable({
    providedIn: 'root'
})
export class MapService {
    onDesignMode: any;
    measurementTool: any;
    snappingManagerTool: any;
    expression: string;
    labelExpressionDefinitionExpression: string;
    angleDefinitionExpression: string;
    annotationDefinitionExpression: string;
    visibilityDefinitionExpression: string;
    featureLayerVisibilityDefinitionExpression: string;
    hiddenDefinitionExpression: string;
    annotationAngleHiddenExpression: string;
    annotationAngleVisibilityExpression: string;
    enlargementDefinitionExpression: string;
    clippingDefinitionExpression: string;
    polylineDefinitionExpression: string;
    clippingMapDefinitionExpression: string;
    clippingFillMapDefinition: string;
    controlPointMapDefinition: string;
    pointMapDefinition: string;
    layers: object;
    private defaultPointSelectionSymbol: any;
    private polylineFeatureLayer: any;
    private defaultLineSelectionSymbol: any;
    private defaultPolygonSelectionSymbol: any;
    private defaultLotSelectionSymbol: any;
    private defaultAffectedLotSelectionSymbol: any;
    private defaultRoadSelectionSymbol: any;
    private defaultInterestSelectionSymbol: any;
    private defaultEnlargementSelectionSymbol: any;
    private defaultClippingSelectionSymbol: any;
    private mapServiceLayersZoomStartTimeout: any;
    private annotationLayerZoomStartTimeout: any;
    private angleLayerZoomStartTimeout: any;
    private mouseDragStartScreenPoint: {x: any; y: any};

    constructor(
        private appConfigService: AppConfigService,
        private mapEventsService: MapEventsService,
        private slipService: SlipService,
        private customLookupService: CustomLookupService,
        private planModelService: PlanModelService,
        private printAreaService: PrintAreaService,
        private mapModeService: MapModeService,
        private mapToolsService: MapToolsService,
        private displayPreferencesService: DisplayPreferencesService,
        private spatialUtilService: SpatialUtilService,
        private lineLayerService: LineLayerService,
        private pointLayerService: PointLayerService,
        private controlPointLayerService: ControlPointLayerService,
        private constructPointLayerService: ConstructPointLayerService,
        private angleLayerService: AngleLayerService,
        private annotationLayerService: AnnotationLayerService,
        private constructLineLayerService: ConstructLineLayerService,
        private polylineLayerService: PolylineLayerService,
        private polygonLayerService: PolygonLayerService,
        private enlargementLayerService: EnlargementLayerService,
        private clippingLayerService: ClippingLayerService,
        private printAreaLayerService: PrintAreaLayerService,
        private lotLayerService: LotLayerService,
        private roadLayerService: RoadLayerService,
        private interestLayerService: InterestLayerService,
        private eventBus: NgEventBus,
        private destroyRef: DestroyRef,
        private esriModuleService: EsriModuleService,
        @Inject(DOCUMENT) private document: Document,
        private lookupService: LookupService,
        private route: ActivatedRoute,
        private router: Router,
        private arcGisAuthService: ArcGisAuthService,
        private ngZone: NgZone
    ) {
        this.eventBus
            .on('setMapMode')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData<boolean>) => {
                this.setMapMode(meta.data, this.displayPreferencesService.displayPreferences);
            });
        this.eventBus
            .on('switchCadastre')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData<boolean>) => {
                this.switchCadastre(meta.data);
            });

        this.eventBus
            .on('deleteMapClickHandler')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.deleteMapClickHandler();
            });

        this.eventBus
            .on('resetMapClickHandler')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.resetMapClickHandler();
            });
    }

    init() {
        this.DEFAULT_BLACK_CURSOR = this.spatialUtilService.getBlackModeDefaultCursorUrl();
        this.DEFAULT_WHITE_CURSOR = this.spatialUtilService.getWhiteModeDefaultCursorUrl();

        this.PENCIL_BLACK_CURSOR = this.spatialUtilService.getBlackModePencilCursorUrl();
        this.PENCIL_WHITE_CURSOR = this.spatialUtilService.getWhiteModePencilCursorUrl();

        this.CROSS_BLACK_CURSOR = this.spatialUtilService.getBlackModeCrossCursorUrl();
        this.CROSS_WHITE_CURSOR = this.spatialUtilService.getWhiteModeCrossCursorUrl();

        this.MOVE_BLACK_CURSOR = this.spatialUtilService.getBlackModeMoveCursorUrl();
        this.MOVE_WHITE_CURSOR = this.spatialUtilService.getWhiteModeMoveCursorUrl();
    }

    refMapScale: number;

    title = 'MapService';

    map = undefined;
    mapServiceLayer = null;

    angleFeatureLayer = null;
    annotationFeatureLayer = null;
    controlPointFeatureLayer = null;
    pointFeatureLayer = null;
    lineFeatureLayer = null;
    polygonFeatureLayer = null;
    lotFeatureLayer = null;
    affectedLotFeatureLayer = null;
    roadFeatureLayer = null;
    constructPointFeatureLayer = null;
    constructLineFeatureLayer = null;
    interestFeatureLayer = null;
    enlargementFeatureLayer = null;
    clippingFeatureLayer = null;
    polylineLineFeatureLayer = null;
    printAreaFeatureLayer = null;

    slipBlackLayers = null;
    slipWhiteLayers = null;
    navToolbar = null;

    projectedCoordSystems = [];

    DEFAULT_BLACK_CURSOR: string;
    DEFAULT_WHITE_CURSOR: string;

    PENCIL_BLACK_CURSOR: string;
    PENCIL_WHITE_CURSOR: string;

    CROSS_BLACK_CURSOR: string;
    CROSS_WHITE_CURSOR: string;

    MOVE_BLACK_CURSOR: string;
    MOVE_WHITE_CURSOR: string;

    initilising = true;

    // $(document).bind('contextmenu', function (e) {
    //     if (
    //         $('#' + this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.ID).is(':visible') &&
    //         (e.toElement.id === 'mainMap_gc' || e.toElement.id === '')
    //     ) {
    //         return false;
    //     }
    // });

    setupMapDefaultConfig() {
        console.debug(this.title + ' : setupMapDefaultConfig');
        //
        // require(['esri/config', 'esri/urlUtils', 'dojo/domReady!'], function (esriConfig, urlUtils) {
        //     esriConfig.defaults.io.corsEnabledServers.push({host: this.appConfigService.getDesignProperties().BASE_URL, withCredentials: true});
        //
        //     esriConfig.defaults.io.alwaysUseProxy = false;
        // });
    }

    resetMapServiceLayer(referenceScale: any, adjustFeatures: any, scaleRatio: any, callback?: () => void) {
        console.debug(this.title + ' : resetMapServiceLayer');

        if (isDefined(this.map)) {
            this.map.removeLayer(this.mapServiceLayer);

            this.addMapServiceLayer(referenceScale, adjustFeatures, scaleRatio);

            this.mapEventsService.mapServiceLayer = this.mapServiceLayer;

            if (callback) {
                callback();
            }
        }
    }

    setMapScale(scale) {
        console.debug(this.title + ' : setMapScale');
        this.map.setScale(scale);
    }

    getMapScale() {
        return this.map.getScale();
    }

    initializeMap(formerTenures, referenceScale, onDesignMode, callback) {
        console.debug(this.title + ' : initializeMap');

        if (this.map) {
            this.map.destroy();
        }
        // TODO ???
        this.map = this.spatialUtilService.map = undefined;
        this.onDesignMode = onDesignMode;
        this.snappingManagerTool = undefined;
        this.expression = `guid = '${this.planModelService.getGuid()}'`;
        this.labelExpressionDefinitionExpression = ' AND labelexpression is not null';
        this.angleDefinitionExpression = ' AND enlargementid is null ';
        this.annotationDefinitionExpression = "   AND (enlargementid is null or featuretype = 'enlargement')  ";
        this.visibilityDefinitionExpression = " AND (visible = 'YES' or visible is null) ";
        this.featureLayerVisibilityDefinitionExpression = " AND (visible = 'YES' or visible is null) ";
        this.hiddenDefinitionExpression = " AND visible = 'NO'";
        this.annotationAngleHiddenExpression = " AND visible = 'NO'";
        this.annotationAngleVisibilityExpression = " AND (visible = 'YES' or visible is null)";
        this.enlargementDefinitionExpression = ' AND id = 0';
        this.clippingDefinitionExpression = ' AND id = 0';
        this.polylineDefinitionExpression = ' AND pointids is not null';
        this.clippingMapDefinitionExpression = ' AND type = 0';
        this.clippingFillMapDefinition = ' AND type = 0';
        this.controlPointMapDefinition = ' AND (type < 6 or type = 100)';
        this.pointMapDefinition = ' AND type >= 6 and type <> 100';

        this.layers = {};

        (window as any).esri.config.defaults.map.zoomDuration = 5;
        (window as any).esri.config.defaults.map.zoomRate = 0;
        (window as any).esri.config.defaults.map.panDuration = 50;
        (window as any).esri.config.defaults.map.panRate = 2.0;

        this.map = this.spatialUtilService.map = new this.esriModuleService.EsriMap(
            this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.ID,
            this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.OPTIONS
        );

        this.map.on('zoom-start', () => {
            if (this.mapServiceLayersZoomStartTimeout) {
                clearTimeout(this.mapServiceLayersZoomStartTimeout);
                this.mapServiceLayersZoomStartTimeout = null;
            }

            if (this.mapServiceLayer.visible) {
                this.mapServiceLayer.setDisableClientCaching(false);
            }

            if (this.slipBlackLayers && this.slipBlackLayers.visible) {
                this.slipBlackLayers.setDisableClientCaching(false);
            }

            if (this.slipWhiteLayers && this.slipWhiteLayers.visible) {
                this.slipWhiteLayers.setDisableClientCaching(false);
            }

            if (
                this.mapServiceLayer.visible ||
                (this.slipBlackLayers && this.slipBlackLayers.visible) ||
                (this.slipWhiteLayers && this.slipWhiteLayers.visible)
            ) {
                this.mapServiceLayersZoomStartTimeout = setTimeout(() => {
                    if (this.mapServiceLayer.visible) {
                        this.mapServiceLayer.setDisableClientCaching(true);
                    }
                    if (this.slipBlackLayers && this.slipBlackLayers.visible) {
                        this.slipBlackLayers.setDisableClientCaching(true);
                    }
                    if (this.slipWhiteLayers && this.slipWhiteLayers.visible) {
                        this.slipWhiteLayers.setDisableClientCaching(true);
                    }
                }, 1000);
            }

            if (this.annotationLayerZoomStartTimeout) {
                clearTimeout(this.annotationLayerZoomStartTimeout);
                this.annotationLayerZoomStartTimeout = null;
            }

            const annotationLayer = this.spatialUtilService.getAnnotationMapLayer();
            if (annotationLayer.visible) {
                const annotationLayerZoomStartTimeoutMs = Math.min(1000, this.spatialUtilService.getAnnotationGraphics().length);
                if (annotationLayerZoomStartTimeoutMs) {
                    annotationLayer.suspend();
                    this.annotationLayerZoomStartTimeout = setTimeout(() => {
                        annotationLayer.resume();
                    }, annotationLayerZoomStartTimeoutMs);
                }
            }

            if (this.angleLayerZoomStartTimeout) {
                clearTimeout(this.angleLayerZoomStartTimeout);
                this.angleLayerZoomStartTimeout = null;
            }

            const angleLayer = this.spatialUtilService.getAngleMapLayer();
            if (angleLayer.visible) {
                const angleLayerZoomStartTimeoutMs = Math.min(1000, this.spatialUtilService.getAngleGraphics().length);
                if (angleLayerZoomStartTimeoutMs) {
                    angleLayer.suspend();
                    this.angleLayerZoomStartTimeout = setTimeout(() => {
                        angleLayer.resume();
                    }, angleLayerZoomStartTimeoutMs);
                }
            }
        });

        this.map.on('mouse-down', evt => {
            this.ngZone.runOutsideAngular(() => {
                if (evt.which === 2) {
                    this.map.setMapCursor(this.getCursorMode() === 'BLACK' ? this.MOVE_BLACK_CURSOR : this.MOVE_WHITE_CURSOR);
                    this.map.disableScrollWheelZoom();
                    this.map.__panStart(evt.screenPoint.x, evt.screenPoint.y);
                    this.mouseDragStartScreenPoint = {x: evt.screenPoint.x, y: evt.screenPoint.y};
                    this.mapToolsService.suspend('MAP_SELECTION');
                }
            });
        });

        this.map.on('mouse-move', evt => {
            this.ngZone.runOutsideAngular(() => {
                if (this.mouseDragStartScreenPoint) {
                    this.map.__pan(evt.screenPoint.x - this.mouseDragStartScreenPoint.x, evt.screenPoint.y - this.mouseDragStartScreenPoint.y);
                }
            });
        });

        this.map.on('mouse-up', evt => {
            this.ngZone.runOutsideAngular(() => {
                if (evt.which === 2) {
                    if (this.mouseDragStartScreenPoint) {
                        this.map.__panEnd(evt.screenPoint.x - this.mouseDragStartScreenPoint.x, evt.screenPoint.y - this.mouseDragStartScreenPoint.y);
                        delete this.mouseDragStartScreenPoint;
                    }

                    this.map.setMapCursor(this.getCursorMode() === 'BLACK' ? this.DEFAULT_BLACK_CURSOR : this.DEFAULT_WHITE_CURSOR);
                    this.map.enableScrollWheelZoom();
                    this.mapToolsService.restore('MAP_SELECTION', {redraw: false});
                }
            });
        });

        const projectedJurisdictionExtent = this.spatialUtilService.project(this.getWAExtent());

        this.map.setExtent(projectedJurisdictionExtent.expand(1.1));
        this.addDefaultLayers();

        this.printAreaService.init(this.printAreaFeatureLayer);

        this.getAllLayers().forEach(l => {
            this.layers[l.id.replace('FeatureLayer', '')] = l;
            this.spatialUtilService.clearAddedDeletedIds(l.id.replace('FeatureLayer', ''));
        });

        this.addMapServiceLayer(referenceScale, false, 1);

        this.mapEventsService.setupMapEvents(this.map);

        if (!this.onDesignMode) {
            callback();
            return;
        }

        const featureLayers = [
            this.pointFeatureLayer,
            this.angleFeatureLayer,
            this.annotationFeatureLayer,
            this.controlPointFeatureLayer,
            this.constructPointFeatureLayer,
            this.lineFeatureLayer,
            this.polygonFeatureLayer,
            this.constructLineFeatureLayer,
            this.polylineFeatureLayer
        ];

        this.addSlipLayers(this.arcGisAuthService.cadastreConfig);

        this.addSelectionSymbolsToDefaultLayers();

        this.mapEventsService.setupNavToolbar(this.map, navToolbar => {
            this.navToolbar = navToolbar;
        });

        this.mapEventsService.setupSelectToolbar(this.map);

        this.map.emit('load', {});

        if (!this.map.loaded || !this.isAllFeaturesClassesLoaded()) {
            return;
        }
        console.debug(this.title + ' : initializeMap - Map has been loaded');

        this.map.layerIds.concat(this.map.graphicsLayerIds).reduce((acc, id) => {
            const layer = this.map.getLayer(id);
            if (!layer) {
                return acc;
            }
            acc[id] = {
                error: layer.on('error', e => {
                    console.debug(`${this.title} : ${id}.on('error')`);
                    acc[id].applyEditsPending = false;
                    acc[id].updatePending = false;
                }),
                'before-apply-edits': layer.on('before-apply-edits', () => {
                    console.debug(`${this.title} : ${id}.on('before-apply-edits')`);
                    acc[id].applyEditsPending = true;
                }),
                'edits-complete': layer.on('edits-complete', () => {
                    console.debug(`${this.title} : ${id}.on('edits-complete')`);
                    acc[id].applyEditsPending = false;
                }),
                'update-start': layer.on('update-start', () => {
                    console.debug(`${this.title} : ${id}.on('update-start')`);

                    // If "update-start" fires after "before-apply-edits", then "edits-complete" will not fire.
                    acc[id].applyEditsPending = false;
                    acc[id].updatePending = true;
                }),
                'update-end': layer.on('update-end', () => {
                    console.debug(`${this.title} : ${id}.on('update-end')`);

                    acc[id].updatePending = false;

                    if (!Object.keys(acc).some(k => acc[k].updatePending)) {
                        console.debug(`${this.title} : ${id}.on('update-end') - all updates complete`);
                        const oldFeaturesMap = Object.keys(acc).reduce((featuresMap, k) => {
                            featuresMap[k] = acc[k].features;
                            return featuresMap;
                        }, {});

                        const newFeaturesMap = Object.keys(acc).reduce((featuresMap, k) => {
                            featuresMap[k] = (this.map.getLayer(k).graphics || [])
                                .filter(g => g.attributes && g.toJson)
                                .map(g => g.toJson())
                                .sort((g1, g2) => g1.attributes.id - g2.attributes.id);
                            return featuresMap;
                        }, {});

                        const ignoreAttrs = ['uniqueId'];
                        const ignoreValues = [null, undefined];
                        if (!equalsIgnoreAttrsValues(newFeaturesMap, oldFeaturesMap, ignoreAttrs, ignoreValues)) {
                            const layers = Object.keys(newFeaturesMap);

                            const featureClassesUpdated = layers
                                .filter(
                                    k =>
                                        oldFeaturesMap[k] && !equalsIgnoreAttrsValues(newFeaturesMap[k], oldFeaturesMap[k], ignoreAttrs, ignoreValues)
                                )
                                .map(k => this.spatialUtilService.getFeatureClass(k))
                                .filter(fc => fc);

                            layers.forEach(k => (acc[k].features = newFeaturesMap[k]));

                            this.eventBus.cast('featuresUpdated', featureClassesUpdated);
                        }
                    }
                })
            };
            return acc;
        }, {});
    }

    moveToExtent(formerTenures) {
        if (this.getMapMode().type === 'mainPlan') {
            const printAreaFeatures = this.spatialUtilService.getPrintAreas();
            if (printAreaFeatures.length && printAreaFeatures[0].geometry) {
                this.map.setExtent(printAreaFeatures[0].geometry.getExtent().expand(1.1));
                return of(null);
            }
            return this.moveToExistingFeaturesExtent(formerTenures);
        }
        return this.moveToExistingFeaturesExtent(formerTenures);
    }

    moveToExistingFeaturesExtent(formerTenures) {
        console.debug(this.title + ' : moveToExistingFeaturesExtent');
        return this.moveToDefaultExtent(formerTenures, this.queryAllFeatures());
    }

    switchCadastre(show) {
        if (!this.slipWhiteLayers || !this.slipBlackLayers) {
            console.debug(`${this.title} - switchCadastre - SLIP layers not loaded`);
            return;
        }

        const viewMode = this.getViewMode();

        if (show) {
            if (viewMode === this.appConfigService.getDesignProperties().VIEW_MODES.BLACK_BACKGROUND_MODE) {
                if (this.slipWhiteLayers && this.map.getLayer(this.slipWhiteLayers.id)) {
                    this.map.removeLayer(this.slipWhiteLayers);
                }
                if (this.slipBlackLayers && !this.map.getLayer(this.slipBlackLayers.id)) {
                    this.map.addLayer(this.slipBlackLayers, 0);
                }
            } else {
                if (this.slipBlackLayers && this.map.getLayer(this.slipBlackLayers.id)) {
                    this.map.removeLayer(this.slipBlackLayers);
                }

                if (viewMode !== this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_MODE) {
                    if (this.slipWhiteLayers && !this.map.getLayer(this.slipWhiteLayers.id)) {
                        this.map.addLayer(this.slipWhiteLayers, 0);
                    }
                } else {
                    if (this.slipWhiteLayers && this.map.getLayer(this.slipWhiteLayers.id)) {
                        this.map.removeLayer(this.slipWhiteLayers);
                    }
                }
            }
        } else {
            if (this.slipBlackLayers && this.map.getLayer(this.slipBlackLayers.id)) {
                this.map.removeLayer(this.slipBlackLayers);
            }
            if (this.slipWhiteLayers && this.map.getLayer(this.slipWhiteLayers.id)) {
                this.map.removeLayer(this.slipWhiteLayers);
            }
        }
    }

    setAdminVisibility(showCadastre) {
        this.switchCadastre(showCadastre);
    }

    clearGraphicToModify() {
        this.mapEventsService.clearGraphicToModify();
    }

    setMapServiceLayerDefinitions() {
        console.debug(this.title + ' : setMapServiceLayerDefinitions');

        const layerDefinitions = [];

        layerDefinitions[0] = this.expression + this.enlargementDefinitionExpression;
        layerDefinitions[1] = this.expression + ' AND type = 1 ' + this.clippingFillMapDefinition;
        layerDefinitions[2] = this.expression + ' AND type = 1 ' + this.clippingFillMapDefinition;
        layerDefinitions[3] = this.expression + 'AND type = 1 ' + this.clippingFillMapDefinition;
        layerDefinitions[4] = this.expression + ' AND type = 2 ' + this.clippingMapDefinitionExpression;
        layerDefinitions[5] = this.expression + ' AND type = 2 ' + this.clippingMapDefinitionExpression;
        layerDefinitions[6] = this.expression + /* this.visibilityDefinitionExpression + */ this.enlargementDefinitionExpression;
        layerDefinitions[7] = this.expression + this.angleDefinitionExpression + this.annotationAngleVisibilityExpression;
        layerDefinitions[8] = this.expression + this.angleDefinitionExpression + this.annotationAngleVisibilityExpression;
        layerDefinitions[9] = this.expression + this.angleDefinitionExpression + this.annotationAngleVisibilityExpression;
        layerDefinitions[10] = this.expression + this.angleDefinitionExpression + this.annotationAngleHiddenExpression;
        layerDefinitions[11] =
            this.expression +
            this.labelExpressionDefinitionExpression +
            this.annotationDefinitionExpression +
            this.annotationAngleVisibilityExpression;
        layerDefinitions[12] =
            this.expression +
            this.labelExpressionDefinitionExpression +
            this.annotationDefinitionExpression +
            this.annotationAngleVisibilityExpression;
        layerDefinitions[13] =
            this.expression +
            this.labelExpressionDefinitionExpression +
            this.annotationDefinitionExpression +
            this.annotationAngleVisibilityExpression;
        layerDefinitions[14] =
            this.expression + this.labelExpressionDefinitionExpression + this.annotationDefinitionExpression + this.annotationAngleHiddenExpression;
        layerDefinitions[15] = this.expression + this.visibilityDefinitionExpression;
        layerDefinitions[16] = this.expression + this.visibilityDefinitionExpression;
        layerDefinitions[17] = this.expression + this.visibilityDefinitionExpression;
        layerDefinitions[18] = this.expression + this.hiddenDefinitionExpression;
        layerDefinitions[19] = this.expression + this.hiddenDefinitionExpression;
        layerDefinitions[20] = this.expression + this.visibilityDefinitionExpression;
        layerDefinitions[21] = this.expression + this.visibilityDefinitionExpression;
        layerDefinitions[22] = this.expression + this.visibilityDefinitionExpression;
        layerDefinitions[23] = this.expression + this.hiddenDefinitionExpression;
        layerDefinitions[24] = this.expression + this.hiddenDefinitionExpression;
        layerDefinitions[25] = this.expression + this.visibilityDefinitionExpression;
        layerDefinitions[26] = this.expression + this.visibilityDefinitionExpression;
        layerDefinitions[27] = this.expression + this.hiddenDefinitionExpression;
        layerDefinitions[28] = this.expression + this.visibilityDefinitionExpression;
        layerDefinitions[29] = this.expression + this.visibilityDefinitionExpression;
        layerDefinitions[30] = this.expression + this.hiddenDefinitionExpression;
        layerDefinitions[31] = this.expression + ' and type = 97';

        this.mapServiceLayer.setLayerDefinitions(layerDefinitions);
    }

    addMapServiceLayer(referenceScale, adjustFeatures, scaleRatio) {
        console.debug(this.title + ' : addMapServiceLayer: ' + ' reference scale: ' + referenceScale + ' scale ratio: ' + scaleRatio);

        scaleRatio = scaleRatio || 1;

        this.refMapScale = referenceScale;

        this.mapServiceLayer = new this.esriModuleService.ArcGISDynamicMapServiceLayer(
            this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.MAP_SERVICE_LAYER.URL +
                '_' +
                this.refMapScale +
                '/MapServer' +
                this.arcGisAuthService.getTokenParam()
        );

        this.mapServiceLayer.id = this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.MAP_SERVICE_LAYER.OPTIONS.id;

        this.setMapServiceLayerDefinitions();

        this.changeViewMode(this.arcGisAuthService.cadastreConfig);

        this.map.addLayer(this.mapServiceLayer);

        this.mapServiceLayer.setVisibility(false);

        if (this.onDesignMode && adjustFeatures && scaleRatio !== 1) {
            this.eventBus.cast('adjustFeatures', {
                mapScale: this.printAreaService.getReferenceScaleDivisor(),
                mapMode: 'mainPlan',
                scaleRatio: scaleRatio
            });
        }
    }

    addDefaultLayers() {
        console.debug(this.title + ' : addDefaultLayers');

        this.angleFeatureLayer = this.angleLayerService.init();
        this.annotationFeatureLayer = this.annotationLayerService.init();
        this.controlPointFeatureLayer = this.controlPointLayerService.init();
        this.pointFeatureLayer = this.pointLayerService.init();
        this.lineFeatureLayer = this.lineLayerService.init();
        this.polygonFeatureLayer = this.polygonLayerService.init();
        this.lotFeatureLayer = this.lotLayerService.init();
        this.affectedLotFeatureLayer = this.spatialUtilService.getAffectedLotMapLayer();
        this.roadFeatureLayer = this.roadLayerService.init();
        this.constructPointFeatureLayer = this.constructPointLayerService.init();
        this.constructLineFeatureLayer = this.constructLineLayerService.init();
        this.interestFeatureLayer = this.interestLayerService.init();
        this.enlargementFeatureLayer = this.enlargementLayerService.init();
        this.clippingFeatureLayer = this.clippingLayerService.init();
        this.polylineFeatureLayer = this.polylineLayerService.init();
        this.printAreaFeatureLayer = this.printAreaLayerService.init();

        this.map.addLayer(this.polygonFeatureLayer);
        this.map.addLayer(this.lotFeatureLayer);
        this.map.addLayer(this.affectedLotFeatureLayer);
        this.map.addLayer(this.roadFeatureLayer);
        this.map.addLayer(this.interestFeatureLayer);
        this.map.addLayer(this.annotationFeatureLayer);
        this.map.addLayer(this.angleFeatureLayer);
        this.map.addLayer(this.lineFeatureLayer);
        this.map.addLayer(this.polylineFeatureLayer);
        this.map.addLayer(this.constructLineFeatureLayer);
        this.map.addLayer(this.pointFeatureLayer);
        this.map.addLayer(this.controlPointFeatureLayer);
        this.map.addLayer(this.constructPointFeatureLayer);
        this.map.addLayer(this.printAreaFeatureLayer);
        this.map.addLayer(this.clippingFeatureLayer);
        this.map.addLayer(this.enlargementFeatureLayer);
    }

    addSlipLayers(cadastreConfig) {
        console.debug(this.title + ' : addSlipLayers');

        const tokenParam = '?token=' + cadastreConfig.token;

        const blackMapConfigItems = JSON.parse(cadastreConfig.blackMapOptions);
        const whiteMapConfigItems = JSON.parse(cadastreConfig.whiteMapOptions);

        blackMapConfigItems.URL = cadastreConfig.blackMapUrl;
        blackMapConfigItems.URL += tokenParam;
        whiteMapConfigItems.URL = cadastreConfig.whiteMapUrl;
        whiteMapConfigItems.URL += tokenParam;

        const coordinateSystem = this.planModelService.getCoordinateSystem();

        if (coordinateSystem.datum !== 'GDA94') {
            blackMapConfigItems.URL = blackMapConfigItems.URL.replace('NLRP_CadastreMapBlackBackground', 'NLRP_CadastreMapBlackBackground_GDA2020');
            whiteMapConfigItems.URL = whiteMapConfigItems.URL.replace('NLRP_CadastreMapWhiteBackground', 'NLRP_CadastreMapWhiteBackground_GDA2020');
        }

        this.slipBlackLayers = new this.esriModuleService.ArcGISDynamicMapServiceLayer(blackMapConfigItems.URL, blackMapConfigItems.BLACK_OPTIONS);
        this.slipBlackLayers.setVisibleLayers(this.appConfigService.getDesignProperties().DESIGN_SETTINGS.SLIP.ADMIN.LAYERS);
        this.slipBlackLayers.setDisableClientCaching(true); //It will increase map delay. However, it will hide layers immediately

        this.slipWhiteLayers = new this.esriModuleService.ArcGISDynamicMapServiceLayer(whiteMapConfigItems.URL, whiteMapConfigItems.WHITE_OPTIONS);
        this.slipWhiteLayers.setVisibleLayers(this.appConfigService.getDesignProperties().DESIGN_SETTINGS.SLIP.ADMIN.LAYERS);
        this.slipWhiteLayers.setDisableClientCaching(true); //It will increase map delay. However, it will hide layers immediately
    }

    getViewMode() {
        return this.mapModeService.viewMode;
    }

    getCursorMode() {
        const viewMode = this.getViewMode();
        let cursorMode = 'BLACK';
        if (viewMode === this.appConfigService.getDesignProperties().VIEW_MODES.BLACK_BACKGROUND_MODE) {
            cursorMode = 'BLACK';
        } else {
            cursorMode = 'WHITE';
        }
        return cursorMode;
    }

    enableZoomInToExtent() {
        console.debug(this.title + ' : enableZoomInToExtent');

        const method = this.getCursorMode();
        this.map.setMapCursor(this['CROSS_' + method + '_CURSOR']);

        this.navToolbar.activate(this.esriModuleService.Navigation.ZOOM_IN);
        this.navToolbar.activated = true;
    }

    deactivateNavToolbar() {
        console.debug(this.title + ' : deactivateNavToolbar');

        this.navToolbar.deactivate();
        this.navToolbar.activated = false;
    }

    getArrayChuncks(array, size) {
        //console.debug(this.title + ' : getArrayChuncks');

        const results = [];
        const arrayTemp = [];
        Array.prototype.push.apply(arrayTemp, array);

        while (arrayTemp.length) {
            results.push(arrayTemp.splice(0, size));
        }

        return results;
    }

    addSelectionSymbolsToDefaultLayers() {
        console.debug(this.title + ' : addSelectionSymbolsToDefaultLayers');

        this.defaultPointSelectionSymbol = new this.esriModuleService.SimpleMarkerSymbol(
            this.esriModuleService.SimpleMarkerSymbol.STYLE_CIRCLE,
            6,
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([0, 255, 255]),
                6
            ),
            new this.esriModuleService.Color([0, 255, 255])
        );

        this.defaultLineSelectionSymbol = new this.esriModuleService.SimpleFillSymbol(
            this.esriModuleService.SimpleFillSymbol.STYLE_NULL,
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([0, 255, 255]),
                6
            ),
            new this.esriModuleService.Color([0, 255, 255])
        );

        this.defaultPolygonSelectionSymbol = new this.esriModuleService.SimpleFillSymbol(
            this.esriModuleService.SimpleFillSymbol.STYLE_SOLID,
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([0, 255, 255, 0.01]),
                2
            ),
            new this.esriModuleService.Color([0, 255, 255, 0.3])
        );

        this.defaultLotSelectionSymbol = new this.esriModuleService.SimpleFillSymbol(
            this.esriModuleService.SimpleFillSymbol.STYLE_NULL,
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([0, 128, 0, 0.01]),
                2
            ),
            new this.esriModuleService.Color([0, 128, 0, 0.2])
        );
        this.defaultLotSelectionSymbol.setOutline(
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([0, 128, 0, 0.5]),
                20
            )
        );

        this.defaultRoadSelectionSymbol = new this.esriModuleService.SimpleFillSymbol(
            this.esriModuleService.SimpleFillSymbol.STYLE_NULL,
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([255, 0, 0, 0.01]),
                2
            ),
            new this.esriModuleService.Color([255, 0, 0, 0.2])
        );
        this.defaultRoadSelectionSymbol.setOutline(
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([255, 0, 0, 0.3]),
                20
            )
        );

        this.defaultInterestSelectionSymbol = new this.esriModuleService.SimpleFillSymbol(
            this.esriModuleService.SimpleFillSymbol.STYLE_NULL,
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([0, 127, 127, 0.2]),
                2
            ),
            new this.esriModuleService.Color([0, 127, 127, 0.2])
        );
        this.defaultInterestSelectionSymbol.setOutline(
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([0, 0, 255, 0.4]),
                20
            )
        );

        this.defaultEnlargementSelectionSymbol = new this.esriModuleService.SimpleFillSymbol(
            this.esriModuleService.SimpleFillSymbol.STYLE_NULL,
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([0, 255, 255, 0.2]),
                2
            ),
            new this.esriModuleService.Color([0, 255, 255, 0.2])
        );
        this.defaultEnlargementSelectionSymbol.setOutline(
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([255, 255, 255, 0.4]),
                20
            )
        );

        this.defaultClippingSelectionSymbol = new this.esriModuleService.SimpleFillSymbol(
            this.esriModuleService.SimpleFillSymbol.STYLE_NULL,
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([0, 255, 255, 0.2]),
                2
            ),
            new this.esriModuleService.Color([0, 255, 255, 0.2])
        );
        this.defaultClippingSelectionSymbol.setOutline(
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([255, 255, 255, 0.4]),
                20
            )
        );

        this.defaultAffectedLotSelectionSymbol = new this.esriModuleService.SimpleMarkerSymbol(
            this.esriModuleService.SimpleMarkerSymbol.STYLE_CIRCLE,
            6,
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([0, 255, 255]),
                6
            ),
            new this.esriModuleService.Color([0, 255, 255])
        );

        this.angleFeatureLayer.setSelectionSymbol(this.defaultPointSelectionSymbol);
        this.annotationFeatureLayer.setSelectionSymbol(this.defaultPointSelectionSymbol);
        this.controlPointFeatureLayer.setSelectionSymbol(this.defaultPointSelectionSymbol);
        this.pointFeatureLayer.setSelectionSymbol(this.defaultPointSelectionSymbol);
        this.lineFeatureLayer.setSelectionSymbol(this.defaultLineSelectionSymbol);
        this.polygonFeatureLayer.setSelectionSymbol(this.defaultPolygonSelectionSymbol);
        this.lotFeatureLayer.setSelectionSymbol(this.defaultLotSelectionSymbol);
        this.affectedLotFeatureLayer.setSelectionSymbol(this.defaultAffectedLotSelectionSymbol);
        this.roadFeatureLayer.setSelectionSymbol(this.defaultRoadSelectionSymbol);
        this.constructPointFeatureLayer.setSelectionSymbol(this.defaultPointSelectionSymbol);
        this.constructLineFeatureLayer.setSelectionSymbol(this.defaultLineSelectionSymbol);
        this.interestFeatureLayer.setSelectionSymbol(this.defaultInterestSelectionSymbol);
        this.enlargementFeatureLayer.setSelectionSymbol(this.defaultEnlargementSelectionSymbol);
        this.clippingFeatureLayer.setSelectionSymbol(this.defaultClippingSelectionSymbol);

        const renderer = new this.esriModuleService.SimpleRenderer({colors: null});

        this.affectedLotFeatureLayer.setRenderer(renderer);
        this.enlargementFeatureLayer.setRenderer(renderer);
        this.clippingFeatureLayer.setRenderer(renderer);

        this.updatePrintAreaFeatureLayerRenderer(true);
    }

    updatePrintAreaFeatureLayerRenderer(showPrintArea) {
        if (!this.printAreaFeatureLayer.renderer) {
            this.printAreaFeatureLayer.setRenderer(
                new this.esriModuleService.SimpleRenderer(
                    new this.esriModuleService.SimpleFillSymbol(
                        this.esriModuleService.SimpleFillSymbol.STYLE_NULL,
                        new this.esriModuleService.SimpleLineSymbol(this.esriModuleService.SimpleLineSymbol.STYLE_NULL, null, 2),
                        null
                    )
                )
            );
        }

        const outline = this.printAreaFeatureLayer.renderer.symbol.outline;
        const mapMode = this.getMapMode();

        if (showPrintArea && mapMode.type === 'mainPlan' && !this.isPrintPreview()) {
            const viewMode = this.getViewMode();
            outline.setColor(
                new this.esriModuleService.Color(
                    viewMode === this.appConfigService.getDesignProperties().VIEW_MODES.BLACK_BACKGROUND_MODE ? [255, 255, 255, 1.0] : [0, 0, 0, 1.0]
                )
            );
            outline.setStyle(this.esriModuleService.SimpleLineSymbol.STYLE_SOLID);
        } else {
            this.printAreaFeatureLayer.renderer = null;
        }

        this.printAreaFeatureLayer.redraw();
    }

    getDefaultExtent(formerTenures, existingFeatures) {
        console.debug(`${this.title} : getDefaultExtent`);

        if (!existingFeatures) {
            existingFeatures = this.getAllFeatures();
        }

        const existingFeaturesExtent = existingFeatures.length ? this.getCalculatedExtentFromFeatures(existingFeatures) : null;

        if (existingFeaturesExtent) {
            return of(this.spatialUtilService.project(existingFeaturesExtent));
        }

        if (this.hasFormerTenureLots(formerTenures)) {
            return this.slipService
                .slipConfig()
                .pipe(
                    concatMap(config =>
                        this.getExtentParamsByFormerTenures(formerTenures, config).pipe(
                            map(formerTenuresExtent => this.spatialUtilService.project(formerTenuresExtent || this.getWAExtent()))
                        )
                    )
                );
        }
        return of(this.spatialUtilService.project(this.getWAExtent()));
    }

    private setMapExtent(extent) {
        if (extent) {
            setTimeout(() => {
                this.map.setExtent(extent.expand(1.1));
            }, 400);
        }
    }

    private setMapExtentToJurisdictionExtent() {
        this.setMapExtent(this.spatialUtilService.project(this.getWAExtent()));
    }

    private setMapExtentToProjectedExtent(extent) {
        const projectedExtent = this.spatialUtilService.project(extent);
        const expandedExtent = projectedExtent ? projectedExtent.expand(1.5) : null;
        this.setMapExtent(expandedExtent);
    }
    moveToDefaultExtent(formerTenures: any[], existingFeatures?: any[]): Observable<boolean> {
        console.debug(`${this.title} : moveToDefaultExtent`);

        existingFeatures = existingFeatures || this.getAllFeatures();
        const existingFeaturesExtent = existingFeatures.length > 0 ? this.getCalculatedExtentFromFeatures(existingFeatures) : null;

        if (existingFeaturesExtent) {
            this.setMapExtentToProjectedExtent(existingFeaturesExtent);
            return of(true);
        }

        if (this.hasFormerTenureLots(formerTenures)) {
            return this.slipService.slipConfig().pipe(
                concatMap(config =>
                    this.getExtentParamsByFormerTenures(formerTenures, config).pipe(
                        tap(formerTenuresExtent => {
                            console.debug(`${this.title} : FormerTenuresExtent:`, formerTenuresExtent);
                            if (formerTenuresExtent) {
                                this.setMapExtentToProjectedExtent(formerTenuresExtent);
                            } else {
                                this.setMapExtentToJurisdictionExtent();
                            }
                        }),
                        catchError(err => {
                            console.error(err);
                            this.setMapExtentToJurisdictionExtent();
                            return of(false);
                        })
                    )
                )
            );
        } else {
            this.setMapExtentToJurisdictionExtent();
            return of(false); // Return an observable that completes immediately
        }
    }

    moveToPlanExtent() {
        const mapMode = this.getMapMode();
        if (mapMode.type === 'mainPlan') {
            const printAreaFeatures = this.spatialUtilService.getPrintAreas();
            if (printAreaFeatures.length && printAreaFeatures[0].geometry) {
                this.map.setExtent(printAreaFeatures[0].geometry.getExtent().expand(1.1));
            } else {
                this.moveToDefaultExtent(this.planModelService.getModel().formerTenures).pipe(take(1)).subscribe();
            }
        } else {
            const projectedExtent = this.spatialUtilService.project(mapMode.enlargementFeature.geometry.getExtent());
            this.map.setExtent(projectedExtent.expand(1.1));
        }
    }

    hasFormerTenureLots(formerTenures) {
        console.debug(this.title + ' : hasFormerTenureLots');

        let hasLots = false;

        if (formerTenures) {
            formerTenures.forEach((formerTenure, index) => {
                if (formerTenure.lots) {
                    formerTenure.lots.forEach(lot => {
                        if (lot.lotId) {
                            hasLots = true;
                        }
                    });
                }
            });
        }

        return hasLots;
    }

    getWAExtent() {
        console.debug(this.title + ' : getWAExtent');

        const coordinateSystem = this.planModelService.getCoordinateSystem();

        const waExtent =
            coordinateSystem.datum === 'GDA94'
                ? this.appConfigService.getDesignProperties().DESIGN_SETTINGS.WA_EXTENT_GDA94
                : this.appConfigService.getDesignProperties().DESIGN_SETTINGS.WA_EXTENT_GDA2020;

        const xmin = waExtent.X_MIN;
        const ymin = waExtent.Y_MIN;
        const xmax = waExtent.X_MAX;
        const ymax = waExtent.Y_MAX;
        const wkid = waExtent.WKID;

        const extent = new (window as any).esri.geometry.Extent(
            xmin,
            ymin,
            xmax,
            ymax,
            new (window as any).esri.SpatialReference({
                wkid: wkid
            })
        );

        return extent;
    }

    getAllFeatures() {
        console.debug(`${this.title} : getAllFeatures`);
        return this.spatialUtilService.getAllFeatures().filter(g => g.geometry);
    }

    getGeometryFeatureLayers() {
        return [
            this.angleFeatureLayer,
            this.annotationFeatureLayer,
            this.controlPointFeatureLayer,
            this.pointFeatureLayer,
            this.lineFeatureLayer,
            this.polygonFeatureLayer,
            this.constructPointFeatureLayer,
            this.constructLineFeatureLayer
        ].filter(v => v);
    }

    getGeometryFeaturesMap() {
        console.debug(`${this.title} - getGeometryFeaturesMap`);

        return this.getGeometryFeatureLayers().reduce((acc, v) => {
            acc[v.id] = acc[v.id] || {};
            v.graphics.filter(g => g.geometry).forEach(g => (acc[v.id][g.attributes.id] = g.toJson()));
            return acc;
        }, {});
    }

    queryAllFeatures() {
        const featureLayers = [
            this.angleFeatureLayer,
            this.annotationFeatureLayer,
            this.controlPointFeatureLayer,
            this.pointFeatureLayer,
            this.lineFeatureLayer,
            this.polygonFeatureLayer,
            this.lotFeatureLayer,
            this.affectedLotFeatureLayer,
            this.roadFeatureLayer,
            this.constructPointFeatureLayer,
            this.constructLineFeatureLayer,
            this.interestFeatureLayer
        ];

        return featureLayers
            .map(featureLayer => this.spatialUtilService.getGraphics(featureLayer))
            .filter(featureLayerResults => featureLayerResults.length)
            .reduce((acc, featureLayerResults) => {
                acc.push(...featureLayerResults);
                return acc;
            }, []);
    }

    isAllFeaturesClassesLoaded() {
        console.debug(this.title + ' : isAllFeaturesClassesLoaded');

        const featuresClassesNotLoaded = [];

        [
            this.angleFeatureLayer,
            this.annotationFeatureLayer,
            this.controlPointFeatureLayer,
            this.pointFeatureLayer,
            this.lineFeatureLayer,
            this.polygonFeatureLayer,
            this.lotFeatureLayer,
            this.affectedLotFeatureLayer,
            this.roadFeatureLayer,
            this.constructPointFeatureLayer,
            this.constructLineFeatureLayer,
            this.interestFeatureLayer,
            this.enlargementFeatureLayer,
            this.clippingFeatureLayer,
            this.polylineFeatureLayer,
            this.printAreaFeatureLayer
        ].forEach((featureLayer, index) => {
            if (!featureLayer || !featureLayer.loaded) {
                featuresClassesNotLoaded.push(featureLayer ? featureLayer.id : index);
            }
        });

        return featuresClassesNotLoaded.length === 0;
    }

    zoomToExtentCenter(extent) {
        console.debug(`${this.title} : zoomToExtentCenter`);

        const projectedExtent = extent.spatialReference.wkid === this.map.spatialReference.wkid ? extent : this.spatialUtilService.project(extent);
        return of(this.map.centerAt(projectedExtent.getCenter()));
    }

    getExtentParamsByFormerTenures(formerTenures: any[], config: any): Observable<any> {
        console.debug(`${this.title} : getExtentParamsByFormerTenures`);

        // Extract land IDs from formerTenures
        const landIds = formerTenures.reduce((acc: number[], formerTenure) => {
            acc.push(...formerTenure.lots.map((lot: any) => lot.lotId).filter((lotId: number) => lotId));
            return acc;
        }, []);
        return this.getSlipFormerTenureExtent(landIds, config).pipe(
            tap(result => {
                if (!result) {
                    throw new Error('No result returned from getSlipFormerTenureExtent');
                }
            }),
            catchError(err => {
                return throwError(() => new Error(err || 'Unknown error occurred'));
            })
        );
    }

    getSlipFormerTenureExtent(landIds: number[], config: any): Observable<any> {
        console.debug(`${this.title} : getSlipFormerTenureExtent`);

        let landIdQuery = 'land_id IN (';

        for (let i = 0; i < landIds.length; i++) {
            landIdQuery += landIds[i] + ',';
        }

        landIdQuery = landIdQuery.substring(0, landIdQuery.length - 1);
        landIdQuery += ')';

        return new Observable(observer => {
            try {
                const queryTask = new this.esriModuleService.QueryTask(`${config.parentTenureQueryUrl}?token=${config.token}`);

                const query = new this.esriModuleService.Query();
                query.returnGeometry = true;
                query.outSpatialReference = {wkid: '102100'}; // Reproject later as needed
                query.where = landIdQuery;

                queryTask.executeForExtent(
                    query,
                    (results: any) => {
                        if (results && results.count) {
                            console.debug(`${this.title} : getSlipFormerTenureExtent - SLIP extent result:`, results.extent);
                            observer.next(this.spatialUtilService.project(results.extent));
                            observer.complete();
                        } else {
                            observer.error('Error reading former tenures geometries');
                        }
                    },
                    (error: any) => {
                        observer.error(`Error reading landid Number. ${error.message}`);
                    }
                );
            } catch (err: any) {
                observer.error(`Error reading lot. Error: ${err.message ? err.message : err}`);
            }
        });
    }
    getCalculatedExtentFromFeatures(features) {
        return this.ngZone.runOutsideAngular(() => {
            console.debug(this.title + ' : getCalculatedExtentFromFeatures');

            let extent = null;

            if (features.length === 1) {
                // Return null if there is only one feature and it has has no extent
                if (!features[0].geometry) {
                    return null;
                }

                const geometry = features[0].geometry;
                extent = geometry.getExtent ? geometry.getExtent() : null;

                if (!extent && geometry.x && geometry.y) {
                    extent = new (window as any).esri.geometry.Extent(geometry.x, geometry.y, geometry.x, geometry.y, geometry.spatialReference);
                }
            } else {
                // Remove features that has no extent
                for (let x = features.length - 1; 0 <= x; x--) {
                    if (
                        !features[x].geometry ||
                        (features[x].geometry.paths && features[x].geometry.paths.length === 0) ||
                        (features[x].geometry.rings && features[x].geometry.rings.length === 0) ||
                        (features[x].attributes && Object.prototype.hasOwnProperty.call(features[x].attributes, 'interestcode'))
                    ) {
                        console.debug('Feature has undefined geometry: ' + JSON.stringify(this.copyFeature(features[x])));

                        features.splice(x, 1);
                    }
                }

                for (let y = 0; y < features.length; y++) {
                    const geometry = features[y].geometry;
                    let tempExtent = geometry.getExtent ? geometry.getExtent() : null;

                    if (!tempExtent && geometry.x && geometry.y) {
                        tempExtent = new (window as any).esri.geometry.Extent(
                            geometry.x,
                            geometry.y,
                            geometry.x,
                            geometry.y,
                            geometry.spatialReference
                        );
                    }

                    if (!extent) {
                        extent = tempExtent;
                    }

                    if (extent && tempExtent) {
                        extent = extent.union(tempExtent);
                    }
                }
            }

            return extent;
        });
    }

    getGeometryFeatureLayerType(featureLayer) {
        let output = '';

        switch (featureLayer.geometryType) {
            case 'esriGeometryPoint':
                output = 'point';
                break;
            case 'esriGeometryPolyline':
                output = 'polyline';
                break;
            case 'esriGeometryPolygon':
                output = 'polygon';
                break;
        }

        return output;
    }
    enableMeasurementTool(tool, opts) {
        console.debug(this.title + ' : enableMeasurementTool');
        opts = opts || {};

        const measurementDivId = 'measurementToolDiv';
        //Destroy previous measurement tool if it exists
        const previousTool = this.esriModuleService.registry.byId(measurementDivId);
        if (previousTool) {
            try {
                previousTool.destroy();
            } catch (err) {
                console.warn('Error destroying previous measurement widget:', err);
            }
            //remove from registry
            this.esriModuleService.registry.remove(measurementDivId);
        }
        //Remove old node if it exists
        const oldDiv = document.getElementById(measurementDivId);
        if (oldDiv?.parentNode) {
            oldDiv.parentNode.removeChild(oldDiv);
        }
        //Recreate new node and measurement tool
        const referenceElement = document.getElementById(this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.ID);
        const measurementToolElement = document.createElement('div');
        measurementToolElement.setAttribute('id', measurementDivId);
        referenceElement.parentNode.insertBefore(measurementToolElement, referenceElement.nextSibling);

        const pointMarker = new this.esriModuleService.PictureMarkerSymbol('/components/design/esri-nlr/images/landgate-pin.png', 17, 26);
        pointMarker.setOffset(0, 13);

        this.measurementTool = new this.esriModuleService.Measurement(
            {
                map: this.map,
                pointSymbol: opts.pointSymbol || pointMarker,
                lineSymbol: opts.lineSymbol,
                _geometryService: this.spatialUtilService.getGeometryService()
            },
            this.esriModuleService.dom.byId('measurementToolDiv')
        );

        this.measurementTool.startup();

        if (!tool || tool === 'distance') {
            const distanceButton = document.querySelector('#measurementToolDiv .esriButton:nth-child(2) > input[type="button"]') as HTMLElement;
            distanceButton.click();
        } else if (tool === 'area') {
            const areaButton = document.querySelector('#measurementToolDiv .esriButton:nth-child(1) > input[type="button"]') as HTMLElement;
            areaButton.click();
        } else if (tool === 'location') {
            const locationButton = document.querySelector('#measurementToolDiv .esriButton:nth-child(3) > input[type="button"]') as HTMLElement;
            locationButton.click();
        }
    }

    enableSnappingTool(featureLayers, opts) {
        console.debug(`${this.title} : enableSnappingTool`);
        opts = opts || {};
        this.disableSnappingTool();
        // keys.copyKey is a 'virtual key' that is either CTRL on Windows, or the META key (a.k.a. CMD key) on Mac.
        // However, it has the wrong code for Chrome on Mac, so we define our own copyKey.
        const copyKey = this.esriModuleService.sniff('mac') ? this.esriModuleService.keys.META : this.esriModuleService.keys.CTRL;

        // Also, if a snapKey isn't specified, the copyKey is used by default.
        // To avoid this default behaviour, provide a snapKey that doesn't exist: CTRL for Mac / META for Windows
        const notAKey = this.esriModuleService.sniff('mac') ? this.esriModuleService.keys.CTRL : this.esriModuleService.keys.META;

        opts = Object.assign(
            {
                snapKey: opts.ctrlKeyDisablesSnapping ? copyKey : notAKey,
                snapPointSymbol: new this.esriModuleService.SimpleMarkerSymbol(
                    this.esriModuleService.SimpleMarkerSymbol.STYLE_CROSS,
                    30,
                    new this.esriModuleService.SimpleLineSymbol(
                        this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                        new this.esriModuleService.Color([255, 0, 0]),
                        2
                    ),
                    new this.esriModuleService.Color([0, 255, 0, 0.25])
                ),
                snapToEdge: false,
                snapToPoint: false,
                snapToVertex: false,
                tolerance: 15
            },
            opts
        );

        this.snappingManagerTool = this.map.enableSnapping({
            map: this.map,
            snapPointSymbol: opts.snapPointSymbol,
            alwaysSnap: true,
            tolerance: opts.tolerance,
            snapKey: opts.snapKey
        });

        const snapLayerInfos = featureLayers.map(featureLayer => ({
            layer: featureLayer,
            snapToEdge: opts.snapToEdge,
            snapToPoint: opts.snapToPoint,
            snapToVertex: opts.snapToVertex
        }));

        this.snappingManagerTool.setLayerInfos(snapLayerInfos);
    }

    disableMeasurementTool() {
        console.debug(this.title + ' : disableMeasurementTool');

        if (this.measurementTool !== undefined) {
            try {
                this.measurementTool.destroy();
            } catch (error) {
                console.error(this.title + ' Error disabling measurement tool. Error: ' + error);
            }

            this.measurementTool = undefined;
        }
    }

    disableSnappingTool() {
        console.debug(this.title + ' : disableSnappingTool');

        if (this.snappingManagerTool !== undefined) {
            this.map.disableSnapping();

            this.snappingManagerTool = undefined;
        }
    }

    getMapMode() {
        return this.mapModeService.mapMode;
    }

    deleteMapClickHandler() {
        console.debug(this.title + ' : deleteMapClickHandler');

        this.mapEventsService.deleteMapClickHandler();
    }

    resetMapClickHandler() {
        console.debug(this.title + ' : resetMapClickHandler');

        this.mapEventsService.resetMapClickHandler(this.map);
    }

    setMapLayersVisibility() {
        const viewMode = this.mapModeService.viewMode;

        if (
            [
                this.appConfigService.getDesignProperties().VIEW_MODES.BLACK_BACKGROUND_MODE,
                this.appConfigService.getDesignProperties().VIEW_MODES.WHITE_BACKGROUND_MODE
            ].includes(viewMode)
        ) {
            this.mapServiceLayer.setVisibility(false);
        } else if (viewMode === this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_MODE) {
            const mapServiceLayerVisible =
                this.router.url.includes('plan') && [null, 'server'].includes(this.route.snapshot.queryParamMap.get('printPreview'));
            this.mapServiceLayer.setVisibility(mapServiceLayerVisible);
        }
    }

    changeViewMode(cadastreConfig, options?) {
        console.debug(this.title + ' : changeViewMode ' + this.arcGisAuthService.cadastreConfig);

        const viewMode = this.mapModeService.viewMode;
        const viewModeResolution = this.mapModeService.viewModeResolution;
        const mapMode = this.getMapMode();

        let imageFormat = viewModeResolution;
        let visibleLayers = undefined;
        let backgroundColor = undefined;

        if (this.displayPreferencesService.displayPreferences) {
            this.setMapLayersVisibility();

            if (viewMode === this.appConfigService.getDesignProperties().VIEW_MODES.BLACK_BACKGROUND_MODE) {
                const blackMapConfigItems = JSON.parse(cadastreConfig.blackMapOptions);
                visibleLayers = blackMapConfigItems.LAYERS;

                if (
                    !this.displayPreferencesService.displayPreferences.showAllAnnotationFeatures &&
                    !this.displayPreferencesService.displayPreferences.showAllGeometryFeatures
                ) {
                    visibleLayers = this.appConfigService.getDesignProperties().VIEW_MODES.BLACK_LAYERS_VISIBLE;
                } else if (
                    this.displayPreferencesService.displayPreferences.showAllAnnotationFeatures &&
                    !this.displayPreferencesService.displayPreferences.showAllGeometryFeatures
                ) {
                    visibleLayers = this.appConfigService.getDesignProperties().VIEW_MODES.BLACK_LAYERS_ANNOTATION_HIDDEN_VISIBLE;
                } else if (
                    !this.displayPreferencesService.displayPreferences.showAllAnnotationFeatures &&
                    this.displayPreferencesService.displayPreferences.showAllGeometryFeatures
                ) {
                    visibleLayers = this.appConfigService.getDesignProperties().VIEW_MODES.BLACK_LAYERS_GEOMETRY_HIDDEN_VISIBLE;
                } else if (
                    this.displayPreferencesService.displayPreferences.showAllAnnotationFeatures &&
                    this.displayPreferencesService.displayPreferences.showAllGeometryFeatures
                ) {
                    visibleLayers = this.appConfigService.getDesignProperties().VIEW_MODES.BLACK_LAYERS_ALL;
                }

                backgroundColor = 'black';
            } else if (viewMode === this.appConfigService.getDesignProperties().VIEW_MODES.WHITE_BACKGROUND_MODE) {
                const whiteMapConfigItems = JSON.parse(this.arcGisAuthService.cadastreConfig.whiteMapOptions);
                visibleLayers = whiteMapConfigItems.LAYERS;

                if (
                    !this.displayPreferencesService.displayPreferences.showAllAnnotationFeatures &&
                    !this.displayPreferencesService.displayPreferences.showAllGeometryFeatures
                ) {
                    visibleLayers = this.appConfigService.getDesignProperties().VIEW_MODES.WHITE_LAYERS_VISIBLE;
                } else if (
                    this.displayPreferencesService.displayPreferences.showAllAnnotationFeatures &&
                    !this.displayPreferencesService.displayPreferences.showAllGeometryFeatures
                ) {
                    visibleLayers = this.appConfigService.getDesignProperties().VIEW_MODES.WHITE_LAYERS_ANNOTATION_HIDDEN_VISIBLE;
                } else if (
                    !this.displayPreferencesService.displayPreferences.showAllAnnotationFeatures &&
                    this.displayPreferencesService.displayPreferences.showAllGeometryFeatures
                ) {
                    visibleLayers = this.appConfigService.getDesignProperties().VIEW_MODES.WHITE_LAYERS_GEOMETRY_HIDDEN_VISIBLE;
                } else if (
                    this.displayPreferencesService.displayPreferences.showAllAnnotationFeatures &&
                    this.displayPreferencesService.displayPreferences.showAllGeometryFeatures
                ) {
                    visibleLayers = this.appConfigService.getDesignProperties().VIEW_MODES.WHITE_LAYERS_ALL;
                }

                backgroundColor = 'white';
            } else if (viewMode === this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_MODE) {
                visibleLayers =
                    mapMode.type === 'mainPlan'
                        ? this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_LAYERS_ALL_MAINPLAN
                        : this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_LAYERS_ALL_ENLARGEMENT;

                if (
                    !this.displayPreferencesService.displayPreferences.showAllAnnotationFeatures &&
                    !this.displayPreferencesService.displayPreferences.showAllGeometryFeatures
                ) {
                    visibleLayers =
                        mapMode.type === 'mainPlan'
                            ? this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_LAYERS_VISIBLE_MAINPLAN
                            : this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_LAYERS_VISIBLE_ENLARGEMENT;
                } else if (
                    this.displayPreferencesService.displayPreferences.showAllAnnotationFeatures &&
                    !this.displayPreferencesService.displayPreferences.showAllGeometryFeatures
                ) {
                    visibleLayers =
                        mapMode.type === 'mainPlan'
                            ? this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_ANNOTATION_HIDDEN_VISIBLE_MAINPLAN
                            : this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_ANNOTATION_HIDDEN_VISIBLE_ENLARGEMENT;
                } else if (
                    !this.displayPreferencesService.displayPreferences.showAllAnnotationFeatures &&
                    this.displayPreferencesService.displayPreferences.showAllGeometryFeatures
                ) {
                    visibleLayers =
                        mapMode.type === 'mainPlan'
                            ? this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_GEOMETRY_HIDDEN_VISIBLE_MAINPLAN
                            : this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_GEOMETRY_HIDDEN_VISIBLE_ENLARGEMENT;
                }

                backgroundColor = 'white';
                imageFormat = 'SVG';
            }

            this.mapServiceLayer.suspend();
            this.mapServiceLayer.setImageFormat(imageFormat, !this.mapServiceLayer.visible);
            if (this.mapServiceLayer.visible) {
                this.mapServiceLayer.setVisibleLayers(visibleLayers);
                this.mapServiceLayer.resume();
            }

            this.spatialUtilService.clearFeatureLayers(options?.clearFeatureClasses || this.spatialUtilService.getFeatureClasses());

            const map = this.document.querySelector('#mainMap') as HTMLElement;
            map.style.backgroundColor = backgroundColor;
            this.spatialUtilService.setMapBackground(backgroundColor);
            this.spatialUtilService.setMapCursor();
        }
    }

    isPrintPreview() {
        return this.getViewMode() === this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_MODE;
    }

    showAllAnnotations = () => this.displayPreferencesService.displayPreferences.showAllAnnotationFeatures || false;
    showAllFeatures = () => this.displayPreferencesService.displayPreferences.showAllGeometryFeatures || false;
    showEnlargementFeatures = () => this.displayPreferencesService.displayPreferences.showEnlargementFeatures || false;
    showClippingFeatures = () => this.displayPreferencesService.displayPreferences.showClippingFeatures || false;
    showPrintArea = () => this.displayPreferencesService.displayPreferences.showPrintArea || false;

    setMapMode(mapModeChanged: any, displayPreferences: DisplayPreferences) {
        console.debug(`${this.title} : setMapMode - mapModeChanged: ${mapModeChanged} - displayPreferences:`, displayPreferences);

        if (this.isPrintPreview()) {
            this.spatialUtilService.unhighlightAllFeatures();
        } else {
            this.spatialUtilService.unhighlightAllNonBusinessFeatures();
        }

        const mapMode = this.getMapMode();

        this.spatialUtilService.setMapMode(mapMode);

        const newRefScale =
            mapMode.type === 'mainPlan' ? this.printAreaService.getReferenceScaleDivisor() : mapMode.enlargementFeature.attributes.scale;

        if (mapModeChanged && this.refMapScale !== newRefScale) {
            this.resetMapServiceLayer(newRefScale, false, 1, () => {
                this.visibilityCallback();
            });
        } else {
            this.visibilityCallback();
        }
    }

    visibilityCallback() {
        const nonBusinessFeatureClasses = this.spatialUtilService.getNonBusinessFeatureClasses();
        const featureClasses = this.spatialUtilService.getFeatureClasses();
        const featureClassesToClear = this.isPrintPreview()
            ? featureClasses
            : featureClasses.filter(
                  fc => nonBusinessFeatureClasses.includes(fc) || this.spatialUtilService.getHighlightedFeatureIds(fc).length === 0
              );
        const mapMode = this.getMapMode();
        this.changeViewMode(this.arcGisAuthService.cadastreConfig, {clearFeatureClasses: featureClassesToClear});

        if (this.showAllAnnotations()) {
            this.featureLayerVisibilityDefinitionExpression = ' ';

            if (this.getMapMode().type === 'mainPlan') {
                this.spatialUtilService.setGraphicsFilter(
                    'annotation',
                    f => f.attributes.labelexpression && (!f.attributes.enlargementid || f.attributes.featuretype === 'enlargement')
                );

                this.spatialUtilService.setGraphicsFilter('angle', f => !f.attributes.enlargementid);

                this.annotationDefinitionExpression = "  AND (enlargementid is null or featuretype = 'enlargement') ";
                this.angleDefinitionExpression = ' AND enlargementid is null';

                this.annotationAngleHiddenExpression = " AND visible = 'NO'";
                this.annotationAngleVisibilityExpression = " AND (visible = 'YES' or visible is null)";
            } else {
                this.spatialUtilService.setGraphicsFilter(
                    'annotation',
                    f => f.attributes.labelexpression && f.attributes.enlargementid === mapMode.id && f.attributes.featuretype !== 'enlargement'
                );

                this.spatialUtilService.setGraphicsFilter('angle', f => f.attributes.enlargementid === mapMode.id);

                this.annotationDefinitionExpression = ' AND enlargementid = ' + mapMode.id + " AND featuretype <> 'enlargement'";
                this.angleDefinitionExpression = ' AND enlargementid = ' + mapMode.id;

                this.annotationAngleHiddenExpression = " AND hiddenonenlargements like '%[" + mapMode.id + "]%'";
                this.annotationAngleVisibilityExpression =
                    " AND NOT (NOT hiddenonenlargements is NULL AND hiddenonenlargements like '%[" + mapMode.id + "]%') ";
            }
        } else {
            this.featureLayerVisibilityDefinitionExpression = " AND (visible = 'YES' or visible is null)";

            if (mapMode.type === 'mainPlan') {
                this.spatialUtilService.setGraphicsFilter(
                    'annotation',
                    f =>
                        f.attributes.labelexpression &&
                        (!f.attributes.enlargementid || f.attributes.featuretype === 'enlargement') &&
                        f.attributes.visible !== 'NO'
                );

                this.spatialUtilService.setGraphicsFilter('angle', f => !f.attributes.enlargementid && f.attributes.visible !== 'NO');

                this.annotationDefinitionExpression = " AND (enlargementid is null or featuretype = 'enlargement') ";
                this.angleDefinitionExpression = ' AND enlargementid is null';

                this.annotationAngleHiddenExpression = " AND visible = 'NO'";
                this.annotationAngleVisibilityExpression = " AND (visible = 'YES' or visible is null)";
            } else {
                this.spatialUtilService.setGraphicsFilter(
                    'annotation',
                    f =>
                        f.attributes.labelexpression &&
                        f.attributes.enlargementid === mapMode.id &&
                        f.attributes.featuretype !== 'enlargement' &&
                        !(f.attributes.hiddenonenlargements && f.attributes.hiddenonenlargements.includes(`[${mapMode.id}]`))
                );

                this.spatialUtilService.setGraphicsFilter(
                    'angle',
                    f =>
                        f.attributes.enlargementid === mapMode.id &&
                        !(f.attributes.hiddenonenlargements && f.attributes.hiddenonenlargements.includes(`[${mapMode.id}]`))
                );

                this.annotationDefinitionExpression = ' AND enlargementid = ' + mapMode.id + " AND featuretype <> 'enlargement'";
                this.angleDefinitionExpression = ' AND enlargementid = ' + mapMode.id;

                this.annotationAngleHiddenExpression = " AND hiddenonenlargements like '%[" + mapMode.id + "]%'";
                this.annotationAngleVisibilityExpression =
                    " AND NOT (NOT hiddenonenlargements is NULL AND hiddenonenlargements like '%[" + mapMode.id + "]%') ";
            }
        }

        let visibleFilter = null;
        if (this.showAllFeatures()) {
            this.featureLayerVisibilityDefinitionExpression = ' ';

            if (mapMode.type === 'mainPlan') {
                this.hiddenDefinitionExpression = " AND visible = 'NO'";
                this.visibilityDefinitionExpression = " AND (visible = 'YES' or visible is null) ";
            } else {
                this.hiddenDefinitionExpression = " AND hiddenonenlargements like '%[" + mapMode.id + "]%'";
                this.visibilityDefinitionExpression =
                    " AND NOT (NOT hiddenonenlargements is NULL AND hiddenonenlargements like '%[" + mapMode.id + "]%') ";
            }
        } else {
            if (mapMode.type === 'mainPlan') {
                visibleFilter = f => f.attributes.visible !== 'NO';
                this.featureLayerVisibilityDefinitionExpression = " AND (visible = 'YES' or visible is null)";
                this.visibilityDefinitionExpression = " AND (visible = 'YES' or visible is null) ";
            } else {
                visibleFilter = f => !(f.attributes.hiddenonenlargements && f.attributes.hiddenonenlargements.includes(`[${mapMode.id}]`));
                this.featureLayerVisibilityDefinitionExpression =
                    " AND NOT (NOT hiddenonenlargements is NULL AND hiddenonenlargements like '%[" + mapMode.id + "]%') ";
                this.visibilityDefinitionExpression =
                    " AND NOT (NOT hiddenonenlargements is NULL AND hiddenonenlargements like '%[" + mapMode.id + "]%') ";
            }
        }

        ['line', 'point', 'polyline', 'constructline', 'constructpoint', 'controlpoint'].forEach(k => {
            this.spatialUtilService.setGraphicsFilter(k, visibleFilter);
            this.spatialUtilService.refreshMapFeatureLayer(k);
        });

        if (this.showEnlargementFeatures()) {
            if (mapMode.type === 'mainPlan') {
                this.spatialUtilService.setGraphicsFilter('enlargement', f => isNumber(f.attributes.id));
                this.enlargementDefinitionExpression = '';
            } else {
                this.spatialUtilService.setGraphicsFilter('enlargement', f => f.attributes.id === mapMode.id);
                this.enlargementDefinitionExpression = ' AND id = ' + mapMode.id;
            }
            this.spatialUtilService.refreshMapFeatureLayer('enlargement');
            this.spatialUtilService.getMapFeatureLayer('enlargement').redraw();
        } else {
            this.spatialUtilService.setGraphicsFilter('enlargement', f => f.attributes.id === 0);
            this.enlargementDefinitionExpression = ' AND id = 0';
            this.spatialUtilService.refreshMapFeatureLayer('enlargement');
            this.spatialUtilService.getMapFeatureLayer('enlargement').redraw();
        }

        if (this.showClippingFeatures()) {
            if (mapMode.type === 'mainPlan') {
                this.clippingDefinitionExpression = '';
                this.clippingMapDefinitionExpression = ' ';
                this.clippingFillMapDefinition = ' AND type = 0';
                this.spatialUtilService.setGraphicsFilter('clipping', f => f.attributes.type === 2);
            } else {
                this.clippingDefinitionExpression = ' AND enlargementid = ' + mapMode.id;
                this.clippingMapDefinitionExpression = this.clippingDefinitionExpression;
                this.clippingFillMapDefinition = this.clippingMapDefinitionExpression;
                this.spatialUtilService.setGraphicsFilter('clipping', f => f.attributes.enlargementid === mapMode.id && f.attributes.type === 1);
            }
            this.spatialUtilService.refreshMapFeatureLayer('clipping');
        } else {
            this.clippingFillMapDefinition = ' AND type = 0';
            if (mapMode.type === 'mainPlan') {
                this.clippingMapDefinitionExpression = ' AND type = 0';
                this.spatialUtilService.setGraphicsFilter('clipping', f => f.attributes.id === 0);
            } else {
                this.clippingMapDefinitionExpression = ' AND enlargementid = ' + mapMode.id;
                this.spatialUtilService.setGraphicsFilter('clipping', f => f.attributes.enlargementid === mapMode.id && f.attributes.type === 2);
            }
            this.clippingDefinitionExpression = ' AND id = 0';
            this.spatialUtilService.refreshMapFeatureLayer('clipping');
        }

        this.updatePrintAreaFeatureLayerRenderer(this.showPrintArea());

        this.setMapServiceLayerDefinitions();

        this.spatialUtilService.refreshMapFeatureLayer('angle');
        this.spatialUtilService.refreshMapFeatureLayer('annotation');
    }

    findFeatureByTypeAndId(featureLayer, id) {
        console.debug(this.title + ' : findFeatureByTypeAndId');

        return this.spatialUtilService.getGraphics(featureLayer).find(f => f.attributes.id === id);
    }

    findFeaturesByTypeAndAttributes(featureLayer, attributeParams, valueParams) {
        console.debug(this.title + ' : findFeaturesByTypeAndAttribute');

        const output = [];

        if (attributeParams.length !== valueParams.length) {
            return output;
        }

        this.spatialUtilService.getGraphics(featureLayer).forEach(feature => {
            let attributesFound = true;
            attributeParams.forEach((attributeParam, index) => {
                const valueParam = valueParams[index];

                if (!this.attributeExists(feature, attributeParam, valueParam)) {
                    attributesFound = false;
                }
            });

            if (attributesFound) {
                output.push(feature);
            }
        });

        return output;
    }

    attributeExists(feature, attributeParam, valueParam) {
        const attribute = feature.attributes[attributeParam];

        if (!attribute || !valueParam || valueParam === '') {
            return false;
        }

        const values = String(valueParam).indexOf(',') !== -1 ? String(valueParam).split(',') : [String(valueParam)];

        const attributeValues = String(attribute).indexOf(',') !== -1 ? String(attribute).split(',') : [String(attribute)];
        const valuesFound = [];

        attributeValues.forEach(attributeValue => {
            values.forEach(value => {
                if (value && typeof value === 'string') {
                    value = value.replace(/\[/g, '').replace(/\]/g, '');
                }

                let attributeValueTemp = window.structuredClone(attributeValue);
                if (attributeValue && typeof attributeValue === 'string') {
                    attributeValueTemp = attributeValue.replace(/\[/g, '').replace(/\]/g, '');
                }

                if (attributeValueTemp === value) {
                    valuesFound.push(feature);
                }
            });
        });

        return valuesFound.length > 0;
    }

    findFeaturesByTypeAndAttribute(featureLayer, attributeParam, valueParam) {
        console.debug(this.title + ' : findFeaturesByTypeAndAttribute');

        return this.spatialUtilService.getGraphics(featureLayer).filter(f => this.attributeExists(f, attributeParam, valueParam));
    }

    getPolygonsByParentFeature(parent) {
        console.debug(`${this.title} : getPolygonsByParentFeature`);

        if (parent.attributes.polygonids) {
            return this.spatialUtilService.getPolygonGraphics().filter(p => parent.attributes.polygonids.includes(`[${p.attributes.id}]`));
        }

        return [];
    }

    getPolylinesByParentFeature(parent) {
        console.debug(`${this.title} : getPolylinesByParentFeature`);

        if (parent.attributes.polylineids) {
            return this.spatialUtilService.getPolylineGraphics().filter(p => parent.attributes.polylineids.includes(`[${p.attributes.id}]`));
        }

        return [];
    }

    copyFeatures(features) {
        const output = [];

        features.forEach(feature => {
            output.push(this.copyFeature(feature));
        });

        return output;
    }

    copyFeature(feature) {
        const output = new this.esriModuleService.Graphic(feature.geometry);
        output.geometry = window.structuredClone(feature.geometry);
        output.attributes = window.structuredClone(feature.attributes);

        if (feature._extent) {
            output._extent = window.structuredClone(feature._extent);
        }

        return output;
    }

    getCustomLookups(path: string, attribute: any) {
        console.debug(this.title + ' : getCustomLookups');
        return this.lookupService.retrieveLookup(path).pipe(
            tap(data => {
                if (attribute.prop.slice(-2) === 'id') {
                    const propFeatureType = attribute.prop.slice(0, -2);
                    const featureLayer = this[propFeatureType + 'FeatureLayer'];

                    if (featureLayer && attribute.featureType !== propFeatureType && Array.isArray(data)) {
                        data.filter(v => {
                            return v.id;
                        }).forEach(v => {
                            const spatialFeatures = this.findFeaturesByTypeAndAttribute(featureLayer, attribute.prop, v.id);
                            if (spatialFeatures.length) {
                                v.id = spatialFeatures[0].attributes.id; //Switch to spatial feature id
                            }
                        });
                    }
                }
            })
        );
    }

    getAllLayers() {
        const layers = this.map.graphicsLayerIds.map(l => this.map.getLayer(l));
        return layers;
    }

    getLayers(layerNames) {
        const layers = layerNames.map(l => this.map.getLayer(l));
        return layers;
    }

    checkIntersect(geometry, graphicGeometry) {
        let result = false;

        result = isFunction(geometry.contains)
            ? geometry.contains(graphicGeometry)
            : isFunction(geometry.extent) && isFunction(geometry.getExtent().intersects)
              ? geometry.getExtent().intersects(graphicGeometry)
              : false;
        return result;
    }
}
